import { useState, type FormEvent } from 'react';
import { useSearch } from '@tanstack/react-router';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';

/**
 * /auth/2fa — TOTP challenge при логине пользователя c включённой 2FA.
 *
 * Production fix 2026-05-06: login.tsx редиректит сюда после получения
 * `{requires_2fa: true, challenge_token}` от POST /api/v1/auth/login.
 * До этой страницы юзер с 2FA попадал в 404 → застревал в login-loop.
 *
 * Семантика — challenge (ввод OTP), НЕ setup. Для первичной настройки
 * используется отдельная /auth/2fa-required (must_setup_2fa flow).
 *
 * Backend контракт (twofa.py:236):
 *   POST /api/v1/auth/2fa/login
 *   IN  : { challenge_token, code }    // code = TOTP 6 цифр или recovery
 *   OUT : { status, needs_tenant_select, needs_context_select,
 *           has_platform_admin, has_tenants }
 *   401 : "Невалидный или истёкший challenge-токен" / "Неверный код 2FA"
 */

interface TwoFALoginResponse {
  status: string;
  needs_tenant_select: boolean;
  needs_context_select: boolean;
  has_platform_admin: boolean;
  has_tenants: boolean;
}

export function TwoFAChallengePage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const challengeToken = search.token ?? '';
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!challengeToken) return;
    setError(null);
    setLoading(true);
    try {
      const resp = await api.post<TwoFALoginResponse>(
        '/api/v1/auth/2fa/login',
        { challenge_token: challengeToken, code: code.trim() },
      );

      // Разводящая логика идентична login.tsx после успешного login.
      // Backend сам считает needs_* флаги — frontend им доверяет.
      if (resp.needs_context_select) {
        window.location.href = '/auth/select-context';
      } else if (resp.has_platform_admin && !resp.has_tenants) {
        window.location.href = '/platform-admin';
      } else if (resp.needs_tenant_select) {
        window.location.href = '/auth/select-tenant';
      } else if (resp.has_tenants) {
        window.location.href = '/dashboard';
      } else {
        window.location.href = '/auth/error?reason=no_access';
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail || 'Неверный код');
      } else {
        setError('Сетевая ошибка');
      }
      setLoading(false);
    }
  };

  if (!challengeToken) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-semibold">
              Сессия входа потеряна
            </CardTitle>
            <CardDescription>
              Challenge-токен не найден в URL. Войдите заново.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full" size="lg">
              <a href="/login">К странице входа</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold flex items-center justify-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Подтверждение входа
          </CardTitle>
          <CardDescription>
            Введите 6-значный код из приложения-аутентификатора
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="totp-code">6-значный код</Label>
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoFocus
                required
                placeholder="123456"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="h-11 text-lg text-center tracking-[0.4em]"
              />
              <p className="text-xs text-muted-foreground">
                Код обновляется каждые 30 секунд. Можно ввести recovery-код
                вместо TOTP.
              </p>
            </div>
            <Button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full"
              size="lg"
            >
              {loading ? 'Проверяем…' : 'Войти'}
            </Button>
            <div className="text-center">
              <a href="/login" className="text-xs text-primary hover:underline">
                Отменить и войти заново
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default TwoFAChallengePage;
